import type {SerializedStyles} from '@emotion/react';
import type {ReactNode} from 'react';

import {css} from '@emotion/react';
import React, {useEffect} from 'react';
import {createPortal} from 'react-dom';

import Close from '../assets/icons/Close';
import useAppTheme from '../styles/useAppTheme';
import useComponent from '../styles/useComponent';
import LoadingIndicator from './LoadingIndicator';

let globalModalCounter = 0;

export type Props = {
  bodyCss?: SerializedStyles;
  children?: ReactNode;
  isLoading?: boolean;
  isOpen?: boolean;
  onCloseClick?: () => void;
};

/**
 * Modal is a reusable component for rendering modal dialogs with customizable content and styles.
 *
 * @component
 * @param {SerializedStyles} props.bodyCss - Optional Emotion styles for the modal body.
 * @param {ReactNode} props.children - The content to display inside the modal.
 * @param {boolean} [props.isLoading=false] - Whether to show a loading indicator overlay.
 * @param {boolean} [props.isOpen=true] - Controls the visibility of the modal.
 * @param {() => void} props.onCloseClick - Callback triggered when the close button is clicked.
 *
 * @remarks
 * - When open, the modal prevents background scrolling by setting `overflow: hidden` on the body. This is reference-counted with a global counter to support multiple modals.
 * - The modal is rendered using ReactDOM.createPortal to `#modal-root` (or document.body if not found).
 * - Use this component for standalone modals. For global modal state and hooks, use the ModalProvider and useModal hook.
 *
 * @example
 * <Modal isOpen={true} isLoading={false} onCloseClick={() => console.log('Closed')}>
 *   <p>Modal Content</p>
 * </Modal>
 */
const Modal = (props: Props) => {
  const {
    bodyCss,
    children,
    isLoading = false,
    isOpen = true,
    onCloseClick,
  } = props;
  const {spacings} = useAppTheme();
  const {modal: modalStyles} = useComponent();

  const finalBodyStyle = bodyCss || modalStyles.body.default;

  useEffect(() => {
    if (isOpen) {
      globalModalCounter++;

      const scrollbarWidth =
        window.innerWidth - document.documentElement.clientWidth;
      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${scrollbarWidth}px`;
      }

      document.body.style.overflow = 'hidden';
    }

    return () => {
      if (isOpen) globalModalCounter--;
      if (globalModalCounter === 0) {
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
      }
    };
  }, [isOpen]);

  if (!isOpen) return;

  const backgroundCss = css({
    inset: 0,
    position: 'fixed',
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 1000 * 100,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  });

  const loadingIndicatorCss = css({
    display: isLoading ? 'block' : 'none',
  });

  const closeButtonCss = css({
    height: spacings.px44,
    width: spacings.px44,
    lineHeight: spacings.px24,
    borderRadius: '100%',
    position: 'absolute',
    backgroundColor: 'white',
    top: 0,
    right: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  });

  const contentCss = css({
    display: 'flex',
    justifyContent: 'center',
    maxHeight: '100%',
    overflowY: 'auto',
    width: '100%',
  });

  const renderLoadingIndicator = () =>
    isLoading && (
      <div css={loadingIndicatorCss}>
        <LoadingIndicator />
      </div>
    );

  const modal = (
    <div css={backgroundCss}>
      {renderLoadingIndicator()}
      <div css={[finalBodyStyle, isLoading && {display: 'none'}]}>
        <button css={closeButtonCss}>
          <Close
            css={{width: spacings.px24, height: spacings.px24}}
            onClick={onCloseClick}
          />
        </button>
        <div css={contentCss}>{children}</div>
      </div>
    </div>
  );

  return createPortal(modal, document.getElementById('modal-root') as Element);
};

export default Modal;
